//
//  SDKTokenComparator.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
import { UnreachableCaseError } from "../../utils/UnreachableCaseError";
import { TokenType } from "../enums/SDKTokenType";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: -  Object Definition
export class TokenComparator {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Comparators
    static isEqualTokenValue(a, b) {
        if (a.tokenType !== b.tokenType) {
            return false;
        }
        switch (a.tokenType) {
            case TokenType.blur:
                return TokenComparator.isEqualBlurTokenValue(a.value, b.value);
            case TokenType.border:
                return TokenComparator.isEqualBorderTokenValue(a.value, b.value);
            case TokenType.color:
                return TokenComparator.isEqualColorTokenValue(a.value, b.value);
            case TokenType.gradient:
                return TokenComparator.isEqualGradientsTokenValue(a.value, b.value);
            case TokenType.dimension:
            case TokenType.size:
            case TokenType.space:
            case TokenType.opacity:
            case TokenType.fontSize:
            case TokenType.lineHeight:
            case TokenType.letterSpacing:
            case TokenType.paragraphSpacing:
            case TokenType.borderWidth:
            case TokenType.radius:
            case TokenType.duration:
            case TokenType.zIndex:
                return TokenComparator.isEqualDimensionTokenValue(a.value, b.value);
            case TokenType.shadow:
                return TokenComparator.isEqualShadowsTokenValue(a.value, b.value);
            case TokenType.string:
            case TokenType.productCopy:
            case TokenType.fontFamily:
            case TokenType.fontWeight:
                return TokenComparator.isEqualStringTokenValue(a.value, b.value);
            case TokenType.textCase:
            case TokenType.textDecoration:
            case TokenType.visibility:
                return TokenComparator.isEqualOptionTokenValue(a.value, b.value);
            case TokenType.typography:
                return TokenComparator.isEqualTypographyTokenValue(a.value, b.value);
            default:
                throw new UnreachableCaseError(a.tokenType, "Unsupported token type for compasion:");
        }
    }
    static referencesSameToken(a, b) {
        // @ts-expect-error TS(2322): Type 'string | boolean | null' is not assignable t... Remove this comment to see the full error message
        return (a.referencedTokenId &&
            b.referencedTokenId &&
            a.referencedTokenId === b.referencedTokenId);
    }
    static anyIsReference(a, b) {
        return ((a.referencedTokenId !== undefined && a.referencedTokenId !== null) ||
            (b.referencedTokenId !== undefined && b.referencedTokenId !== null));
    }
    static isUndefinedValue(a, b) {
        return !a && !b;
    }
    static isEqualBlurTokenValue(a, b) {
        if (this.isUndefinedValue(a, b) || this.referencesSameToken(a, b)) {
            return true;
        }
        return (a.type === b.type && this.isEqualDimensionTokenValue(a.radius, b.radius));
    }
    static isEqualBorderTokenValue(a, b) {
        if (this.isUndefinedValue(a, b) || this.referencesSameToken(a, b)) {
            return true;
        }
        return (this.isEqualColorTokenValue(a.color, b.color) &&
            this.isEqualDimensionTokenValue(a.width, b.width) &&
            a.position === b.position);
    }
    static isEqualColorTokenValue(a, b) {
        // if (this.anyIsReference(a, b)) {
        //     return this.referencesSameToken(a,b)
        // }
        return (a.color.r === b.color.r &&
            a.color.g === b.color.g &&
            a.color.b === b.color.b &&
            a.opacity.measure === b.opacity.measure);
    }
    static isEqualGradientsTokenValue(a, b) {
        // FIXME: Deep layers, 1 -> [2,3,4]; 5->[6,4], 6->[2,3]
        if (a.length !== b.length) {
            return false;
        }
        for (let i = 0; i < a.length; i += 1) {
            if (!this.isEqualGradientTokenValue(a[i], b[i])) {
                return false;
            }
        }
        return true;
    }
    static isEqualGradientTokenValue(a, b) {
        if (this.isUndefinedValue(a, b) || this.referencesSameToken(a, b)) {
            return true;
        }
        return (a.aspectRatio === b.aspectRatio &&
            a.from.x === b.from.x &&
            a.from.y === b.from.y &&
            a.to.x === b.to.x &&
            a.to.y === b.to.y &&
            a.type === b.type &&
            this.isEqualGradientStops(a.stops, b.stops));
    }
    static isEqualGradientStops(a, b) {
        if (a.length !== b.length) {
            return false;
        }
        for (let i = 0; i < a.length; i += 1) {
            const as = a[i];
            const bs = b[i];
            if (!this.isEqualColorTokenValue(as.color, bs.color) ||
                as.position !== bs.position) {
                return false;
            }
        }
        return true;
    }
    static isEqualDimensionTokenValue(a, b) {
        // If this.referencesSameToken(a, b) === true, they could still reference token, that has override
        // Base:  typography.fontSize = {fs.default}, fs.default = 10
        // Theme: typography.fontSize = {fs.default}, fs.default = 11
        // Thus they reference same token, but in theme it has different value.
        // Which makes its parent (typography) being ignored as override candidate.
        // This concerns all tokens that could be used as properties inside complex tokens (dim, string, options).
        if (this.isUndefinedValue(a, b)) {
            return true;
        }
        if (!a || !b) {
            return false;
        }
        return a.measure === b.measure && a.unit === b.unit;
    }
    static isEqualShadowsTokenValue(a, b) {
        // FIXME: Deep layers, 1 -> [2,3,4]; 5->[6,4], 6->[2,3]
        if (a.length !== b.length) {
            return false;
        }
        for (let i = 0; i < a.length; i += 1) {
            if (!this.isEqualShadowTokenValue(a[i], b[i])) {
                return false;
            }
        }
        return true;
    }
    static isEqualShadowTokenValue(a, b) {
        if (this.isUndefinedValue(a, b) || this.referencesSameToken(a, b)) {
            return true;
        }
        return (this.isEqualColorTokenValue(a.color, b.color) &&
            a.radius === b.radius &&
            a.spread === b.spread &&
            a.x === b.x &&
            a.y === b.y &&
            a.type === b.type &&
            this.isEqualDimensionTokenValue(a.opacity, b.opacity));
    }
    // We don't really have a way to distuingish String from ProductCopy just by value here as we store type above the hierarchy
    // As long as this function is used as part of isEqualTokenValue where we check tokenType it is safe
    static isEqualStringTokenValue(a, b) {
        // If this.referencesSameToken(a, b) === true, they could still reference token, that has override
        // Base:  typography.fontFamily = {ff.default}, ff.default = Roboto
        // Theme: typography.fontFamily = {ff.default}, ff.default = Gotham
        // Thus they reference same token, but in theme it has different value.
        // Which makes its parent (typography) being ignored as override candidate.
        // This concerns all tokens that could be used as properties inside complex tokens (dim, string, options).
        if (this.isUndefinedValue(a, b)) {
            return true;
        }
        return a.text === b.text;
    }
    static isEqualOptionTokenValue(a, b) {
        // If this.referencesSameToken(a, b) === true, they could still reference token, that has override
        // Base:  typography.textCase = {tc.default}, tc.default = None
        // Theme: typography.textCase = {tc.default}, tc.default = Upper
        // Thus they reference same token, but in theme it has different value.
        // Which makes its parent (typography) being ignored as override candidate.
        // This concerns all tokens that could be used as properties inside complex tokens (dim, string, options).
        if (this.isUndefinedValue(a, b)) {
            return true;
        }
        // NOTE: We do not rely on options always be present
        return a.value === b.value;
    }
    static isEqualTypographyTokenValue(a, b) {
        if (this.isUndefinedValue(a, b) || this.referencesSameToken(a, b)) {
            return true;
        }
        return (this.isEqualStringTokenValue(a.fontFamily, b.fontFamily) &&
            this.isEqualStringTokenValue(a.fontWeight, b.fontWeight) &&
            this.isEqualDimensionTokenValue(a.fontSize, b.fontSize) &&
            this.isEqualDimensionTokenValue(a.letterSpacing, b.letterSpacing) &&
            this.isEqualDimensionTokenValue(a.lineHeight, b.lineHeight) &&
            this.isEqualDimensionTokenValue(a.paragraphIndent, b.paragraphIndent) &&
            this.isEqualDimensionTokenValue(a.paragraphSpacing, b.paragraphSpacing) &&
            a.textCase.value === b.textCase.value &&
            a.textDecoration.value === b.textDecoration.value);
    }
}
